import { useMeasurementPresentation } from '../Measurement'
import { useState, useMemo, useRef } from 'react'
import {
    MagnifyingGlassIcon,
    XMarkIcon,
    TruckIcon,
    CheckIcon,
    Squares2X2Icon,
    ListBulletIcon,
    FunnelIcon,
    ArrowPathIcon,
    CubeIcon,
    ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { Package, Snowflake, Truck, TriangleAlert, Van, Thermometer, Weight, SquareDimensions, type LucideIcon } from 'lucide-react'
import Dialog from '../ui/Dialog'
import { Vehicle as VehicleSettings } from '../../services/vehicle.service'
import StarRating from '../ui/StarRating'
import { getVehicleSubtypeLabel, getVehicleTypeLabel, getVehicleDisplayName } from '../../lib/vehicleHelpers'
import truckImage from '../../assets/vehicles/tracteur_4x2.png'
import rigidTruckImage from '../../assets/vehicles/camion_porteur_2_essieux.png'
import semiTrailerImage from '../../assets/vehicles/semi_remorque_2_essieux.png'
import trailerImage from '../../assets/vehicles/remorque_2_essieux.png'
import containerImage from '../../assets/vehicles/conteneur_20p.jpg'
import vanImage from '../../assets/vehicles/fourgonnettes.png'

interface VehicleSearchDialogProps {
    isOpen: boolean
    onClose: () => void
    vehicles: VehicleSettings[]
    isLoading: boolean
    currentVehicleId?: string | null
    currentVehicleReg?: string | null
    onSelectVehicle: (vehicle: VehicleSettings) => void
    title?: string
    error?: boolean
    onRetry?: () => void
}

type CategoryFilter = 'ALL' | 'TRUCK' | 'RIGID_TRUCK' | 'SEMI_TRAILER' | 'TRAILER' | 'CONTAINER' | 'VAN'

function getEffectiveVehicleType(vehicle: Pick<VehicleSettings, 'type' | 'vehicle_subtype'>): VehicleSettings['type'] {
    if (vehicle.type === 'SEMI_TRAILER' && vehicle.vehicle_subtype?.startsWith('remorque_')) {
        return 'TRAILER'
    }
    return vehicle.type
}

export default function VehicleSearchDialog({
    isOpen,
    onClose,
    vehicles,
    isLoading,
    currentVehicleId,
    currentVehicleReg,
    onSelectVehicle,
    title = 'Sélectionner un équipement du parc',
    error = false,
    onRetry,
}: VehicleSearchDialogProps) {
    const searchInput = useRef<HTMLInputElement>(null)
    const [searchQuery, setSearchQuery] = useState('')
    const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('ALL')
    const [hasReeferFilter, setHasReeferFilter] = useState(false)
    const [isAdrFilter, setIsAdrFilter] = useState(false)
    const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid')

    // Filter logic
    const filteredVehicles = useMemo(() => {
        return vehicles.filter((v) => {
            // Query match (Registration, Brand, Model, Subtype)
            const q = searchQuery.toLowerCase().trim()
            const matchesQuery =
                !q ||
                v.registration_number?.toLowerCase().includes(q) ||
                v.brand?.toLowerCase().includes(q) ||
                v.model?.toLowerCase().includes(q) ||
                v.vehicle_subtype?.toLowerCase().includes(q) ||
                v.type?.toLowerCase().includes(q)

            // Category filter match
            const matchesCategory =
                categoryFilter === 'ALL' || getEffectiveVehicleType(v) === categoryFilter

            // Reefer (Frigo) match
            const matchesReefer = !hasReeferFilter || !!v.has_reefer_unit

            // ADR match
            const matchesAdr = !isAdrFilter || !!v.is_hazardous_compatible

            return matchesQuery && matchesCategory && matchesReefer && matchesAdr
        })
    }, [vehicles, searchQuery, categoryFilter, hasReeferFilter, isAdrFilter])

    // Count metrics for category badges
    const categoryCounts = useMemo(() => {
        return {
            TRUCK: vehicles.filter((v) => v.type === 'TRUCK').length,
            RIGID_TRUCK: vehicles.filter((v) => v.type === 'RIGID_TRUCK').length,
            SEMI_TRAILER: vehicles.filter((v) => getEffectiveVehicleType(v) === 'SEMI_TRAILER').length,
            TRAILER: vehicles.filter((v) => getEffectiveVehicleType(v) === 'TRAILER').length,
            CONTAINER: vehicles.filter((v) => v.type === 'CONTAINER').length,
            VAN: vehicles.filter((v) => v.type === 'VAN').length,
        }
    }, [vehicles])

    const handleResetFilters = () => {
        setSearchQuery('')
        setCategoryFilter('ALL')
        setHasReeferFilter(false)
        setIsAdrFilter(false)
    }

    const { format: formatMeasure } = useMeasurementPresentation()
    const categoryOptions: { id: CategoryFilter; label: string; icon: LucideIcon; image?: string; count: number }[] = [
        { id: 'SEMI_TRAILER', label: 'Semi-remorques', icon: Truck, image: semiTrailerImage, count: categoryCounts.SEMI_TRAILER },
        { id: 'TRAILER', label: 'Remorques', icon: Truck, image: trailerImage, count: categoryCounts.TRAILER },
        { id: 'TRUCK', label: 'Tracteurs', icon: Truck, image: truckImage, count: categoryCounts.TRUCK },
        { id: 'RIGID_TRUCK', label: 'Porteurs rigides', icon: Truck, image: rigidTruckImage, count: categoryCounts.RIGID_TRUCK },
        { id: 'CONTAINER', label: 'Conteneurs', icon: Package, image: containerImage, count: categoryCounts.CONTAINER },
        { id: 'VAN', label: 'Fourgonnettes', icon: Van, image: vanImage, count: categoryCounts.VAN },
    ]

    // Format dimensions (mm -> m)
    const formatDim = (val?: number | string | null) => {
        if (val == null || val === '') return '—'
        const num = Number(val)
        if (isNaN(num) || num <= 0) return '—'
        return formatMeasure(num, 'mm', 'vehicleLength')
    }

    // Format weight (kg -> tonnes)
    const formatWeightTonnes = (val?: number | string | null) => {
        if (val == null || val === '') return '—'
        const num = Number(val)
        if (isNaN(num) || num <= 0) return '—'
        return formatMeasure(num, 'kg', 'mass')
    }

    // Compute Volume in m³ if length, width, height are available
    const computeVolumeM3 = (v: VehicleSettings) => {
        if (v.capacity_volume != null) {
            const numVol = Number(v.capacity_volume)
            if (!isNaN(numVol) && numVol > 0) {
                return numVol
            }
        }
        const l = Number(v.length)
        const w = Number(v.width)
        const h = Number(v.height)
        if (!isNaN(l) && !isNaN(w) && !isNaN(h) && l > 0 && w > 0 && h > 0) {
            return (l * w * h) / 1_000_000_000
        }
        return null
    }

    // Vehicle type icon helper
    const getVehicleBadge = (type: string) => {
        switch (type) {
            case 'TRUCK':
                return { label: getVehicleTypeLabel(type), emoji: '🚛', color: 'bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300 border-blue-200 dark:border-blue-800' }
            case 'RIGID_TRUCK':
                return { label: getVehicleTypeLabel(type), emoji: '🚛', color: 'bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300 border-blue-200 dark:border-blue-800' }
            case 'SEMI_TRAILER':
                return { label: getVehicleTypeLabel(type), emoji: '🚚', color: 'bg-purple-50 text-purple-700 dark:bg-purple-950/50 dark:text-purple-300 border-purple-200 dark:border-purple-800' }
            case 'TRAILER':
                return { label: getVehicleTypeLabel(type), emoji: '🚚', color: 'bg-purple-50 text-purple-700 dark:bg-purple-950/50 dark:text-purple-300 border-purple-200 dark:border-purple-800' }
            case 'CONTAINER':
                return { label: 'Conteneur', emoji: '📦', color: 'bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300 border-amber-200 dark:border-amber-800' }
            case 'VAN':
                return { label: 'Fourgonnette', emoji: '🚐', color: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800' }
            default:
                return { label: type || 'Véhicule', emoji: '🚛', color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700' }
        }
    }

    const getVehicleTypeStyle = (type: string | undefined) => {
        switch (type) {
            case 'CONTAINER':
                return { icon: CubeIcon, color: '#0ea5e9' }
            case 'SEMI_TRAILER':
                return { icon: TruckIcon, color: '#f59e0b' }
            case 'RIGID_TRUCK':
                return { icon: TruckIcon, color: '#10b981' }
            case 'VAN':
                return { icon: TruckIcon, color: '#8b5cf6' }
            case 'TRAILER':
                return { icon: TruckIcon, color: '#6366f1' }
            case 'TRUCK':
                return { icon: TruckIcon, color: '#f97316' }
            default:
                return { icon: TruckIcon, color: '#94a3b8' }
        }
    }

    const getStatusBadgeClasses = (status: string | undefined) => {
        switch (status) {
            case 'active':
                return 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400'
            case 'maintenance':
                return 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
            case 'inactive':
                return 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400'
            default:
                return 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-400'
        }
    }

    const getStatusLabel = (status: string | undefined) => {
        if (!status) return '-'
        const labels: Record<string, string> = {
            active: 'Actif',
            inactive: 'Inactif',
            maintenance: 'Maintenance',
            out_of_service: 'Hors service',
        }
        return labels[status] || status
    }

    return (
        <Dialog
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            initialFocus={searchInput}
            size="lg"
        >
            <div className="space-y-4">
                {error && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">Le parc d’équipements est indisponible. <button type="button" onClick={onRetry} className="min-h-9 underline">Réessayer</button></div>}
                {/* ── SEARCH & CATEGORY BAR ─────────────────────────────────────────── */}
                <div className="space-y-3">
                    {/* Main Search Input & View Toggle */}
                    <div className="flex items-center gap-3">
                        <div className="relative flex-1">
                            <MagnifyingGlassIcon className="w-5 h-5 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                ref={searchInput}
                                type="search"
                                aria-label="Rechercher dans le parc d’équipements"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Rechercher par immatriculation, marque, modèle, type (ex: TN-1234, Schmitz, Semi)..."
                                className="w-full pl-10 pr-9 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all shadow-xs"
                                autoFocus
                            />
                            {searchQuery && (
                                <button
                                    type="button"
                                    onClick={() => setSearchQuery('')}
                                    aria-label="Effacer la recherche du parc"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1"
                                >
                                    <XMarkIcon className="w-4 h-4" />
                                </button>
                            )}
                        </div>

                        {/* View Mode Toggle */}
                        <div className="flex items-center bg-slate-100 dark:bg-slate-800/80 p-1 rounded-xl border border-slate-200 dark:border-slate-700 flex-shrink-0">
                            <button
                                type="button"
                                onClick={() => setViewMode('grid')}
                                aria-pressed={viewMode === 'grid'}
                                className={`p-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-all ${viewMode === 'grid'
                                    ? 'bg-white dark:bg-slate-700 text-orange-700 dark:text-orange-300 shadow-xs'
                                    : 'text-slate-500 hover:text-slate-900 dark:text-slate-400'
                                    }`}
                                title="Vue Cartes"
                            >
                                <Squares2X2Icon className="w-4 h-4" />
                            </button>
                            <button
                                type="button"
                                onClick={() => setViewMode('table')}
                                aria-pressed={viewMode === 'table'}
                                className={`p-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-all ${viewMode === 'table'
                                    ? 'bg-white dark:bg-slate-700 text-orange-700 dark:text-orange-300 shadow-xs'
                                    : 'text-slate-500 hover:text-slate-900 dark:text-slate-400'
                                    }`}
                                title="Vue Liste Compacte"
                            >
                                <ListBulletIcon className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Vehicle type filters */}
                    <div className="flex flex-wrap items-center gap-2 pb-1">
                        {categoryOptions.map(({ id, label, icon: Icon, image, count }) => (
                            <button
                                key={id}
                                type="button"
                                onClick={() => setCategoryFilter((current) => current === id ? 'ALL' : id)}
                                aria-pressed={categoryFilter === id}
                                title={label}
                                className={`group relative flex h-28 w-28 shrink-0 flex-col items-center justify-center overflow-hidden rounded-xl border-2 bg-white text-xs font-semibold transition-all ${categoryFilter === id
                                    ? 'border-orange-600 text-white shadow-xs ring-2 ring-orange-500/30'
                                    : 'border-white text-slate-600 hover:border-slate-100 dark:border-white dark:text-slate-300 dark:hover:border-slate-200'
                                    }`}
                            >
                                {image ? (
                                    <>
                                        <img
                                            src={image}
                                            alt=""
                                            aria-hidden="true"
                                            className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                                        />
                                        <span className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-black/85 to-transparent" />
                                    </>
                                ) : (
                                    <Icon className="h-8 w-8 shrink-0" aria-hidden="true" />
                                )}
                                <span className={`absolute inset-x-0 bottom-0 truncate px-2 pb-2 pt-5 text-center ${image ? 'text-white' : ''}`}>{label}</span>
                                <span
                                    className={`absolute right-2 top-2 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${image
                                        ? 'bg-black/60 text-white'
                                        : categoryFilter === id
                                            ? 'bg-blue-600/15 text-blue-700 dark:text-blue-200'
                                        : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                                        }`}
                                >
                                    {count}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Equipment filters */}
                    <div className="flex flex-wrap items-center justify-between gap-2 pt-2 text-xs border-t border-slate-100 dark:border-slate-800/80">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-slate-500 dark:text-slate-400 flex items-center gap-1">
                                <FunnelIcon className="w-3.5 h-3.5" />
                                Équipements
                            </span>

                            <button
                                type="button"
                                onClick={() => setHasReeferFilter((enabled) => !enabled)}
                                aria-pressed={hasReeferFilter}
                                title="Filtrer les véhicules équipés d'un groupe frigorifique"
                                className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px] font-bold transition-colors ${hasReeferFilter
                                    ? 'border-blue-500 bg-blue-500 text-white shadow-xs'
                                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
                                    }`}
                            >
                                <Snowflake className="h-4 w-4" aria-hidden="true" />
                                FRIGO
                            </button>
                            <button
                                type="button"
                                onClick={() => setIsAdrFilter((enabled) => !enabled)}
                                aria-pressed={isAdrFilter}
                                title="Filtrer les véhicules compatibles ADR"
                                className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px] font-bold transition-colors ${isAdrFilter
                                    ? 'border-red-500 bg-red-500 text-white shadow-xs'
                                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
                                    }`}
                            >
                                <TriangleAlert className="h-4 w-4" aria-hidden="true" />
                                ADR
                            </button>
                        </div>

                        {/* Results count */}
                        <div className="text-slate-500 dark:text-slate-400 font-mono font-medium text-[11px]">
                            {filteredVehicles.length} / {vehicles.length} véhicules
                        </div>
                    </div>
                </div>

                {/* ── RESULTS DISPLAY AREA ─────────────────────────────────────────── */}
                <div className="min-h-[320px] max-h-[500px] overflow-y-auto pr-1 pt-1">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                            <ArrowPathIcon className="w-8 h-8 animate-spin text-blue-500 mb-2" />
                            <p className="text-sm font-semibold">Chargement des véhicules du parc...</p>
                        </div>
                    ) : filteredVehicles.length === 0 ? (
                        <div className="text-center py-16 px-4 bg-slate-50/50 dark:bg-slate-900/30 rounded-2xl border border-dashed border-slate-200 dark:border-slate-800">
                            <TruckIcon className="w-14 h-14 mx-auto text-slate-300 dark:text-slate-600 stroke-[1.2] mb-3" />
                            <h4 className="text-base font-bold text-slate-900 dark:text-white">
                                Aucun véhicule ne correspond à vos critères
                            </h4>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-sm mx-auto">
                                Essayez d'ajuster votre terme de recherche ou de réinitialiser les filtres Frigo/ADR et catégories.
                            </p>
                            <button
                                type="button"
                                onClick={handleResetFilters}
                                className="mt-4 px-4 py-2 text-xs font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800 rounded-xl hover:bg-blue-100 transition-colors"
                            >
                                Réinitialiser tous les filtres
                            </button>
                        </div>
                    ) : viewMode === 'grid' ? (
                        /* ── GRID CARDS VIEW ────────────────────────────────────────── */
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {filteredVehicles.map((vehicle) => {
                                const isSelected =
                                    currentVehicleId === vehicle.id ||
                                    (currentVehicleReg && vehicle.registration_number === currentVehicleReg)
                                const badge = getVehicleBadge(getEffectiveVehicleType(vehicle))
                                const volM3 = computeVolumeM3(vehicle)

                                return (
                                    <div
                                        key={vehicle.id || vehicle.registration_number || undefined}
                                        onClick={() => {
                                            onSelectVehicle(vehicle)
                                            onClose()
                                        }}
                                        className={`group relative rounded-2xl border p-4 transition-all duration-200 cursor-pointer flex flex-col justify-between ${isSelected
                                            ? 'bg-orange-50/70 dark:bg-orange-950/20 border-orange-400 dark:border-orange-600 shadow-sm ring-2 ring-orange-500/10'
                                            : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-orange-300 dark:hover:border-orange-700 hover:shadow-sm'
                                            }`}
                                    >
                                        {/* Header line: Immatriculation & Badges */}
                                        <div>
                                            <div className="flex items-start justify-between gap-2 mb-2">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xl">{badge.emoji}</span>
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-mono text-base font-extrabold text-slate-900 dark:text-white tracking-tight">
                                                                {getVehicleDisplayName(vehicle)}
                                                            </span>
                                                            {(vehicle.favorite_rating || 0) > 0 && (
                                                                <span className="text-amber-400 text-xs font-bold flex items-center gap-0.5" title="Véhicule favori">
                                                                    ⭐ {vehicle.favorite_rating}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {(vehicle.brand || vehicle.model) && (
                                                            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                                                                {vehicle.brand} {vehicle.model}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Selected Indicator */}
                                                {isSelected && (
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-extrabold bg-blue-600 text-white shadow-xs">
                                                        <CheckIcon className="w-3.5 h-3.5 stroke-[3]" />
                                                        Actif
                                                    </span>
                                                )}
                                            </div>

                                            {/* Type & Subtype Pills */}
                                            <div className="flex items-center gap-1.5 flex-wrap mb-3">
                                                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-md border ${badge.color}`}>
                                                    {badge.label}
                                                </span>
                                                {vehicle.vehicle_subtype && (
                                                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
                                                        {getVehicleSubtypeLabel(vehicle.vehicle_subtype)}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Spec Grid */}
                                            <div className="grid grid-cols-3 gap-2 bg-slate-50 dark:bg-slate-800/60 rounded-xl p-2.5 text-xs mb-3 border border-slate-100 dark:border-slate-800">
                                                <div>
                                                    <span className="text-[10px] text-slate-400 uppercase font-semibold block">Dimensions</span>
                                                    <span className="font-mono font-bold text-slate-800 dark:text-slate-200">
                                                        {formatDim(vehicle.length)} × {formatDim(vehicle.width)} × {formatDim(vehicle.height)}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="text-[10px] text-slate-400 uppercase font-semibold block">Charge utile</span>
                                                    <span className="font-mono font-bold text-slate-800 dark:text-slate-200">
                                                        {formatWeightTonnes(vehicle.capacity_weight)}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="text-[10px] text-slate-400 uppercase font-semibold block">Volume max</span>
                                                    <span className="font-mono font-bold text-slate-800 dark:text-slate-200">
                                                        {volM3 ? formatMeasure(volM3, 'm3', 'volume') : '—'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Footer Row: Special Equipment Badges & Apply Button */}
                                        <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-800/80">
                                            <div className="flex items-center gap-1.5">
                                                {vehicle.has_reefer_unit ? (
                                                    <span className="px-2 py-0.5 text-[10px] font-bold rounded-md bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 flex items-center gap-1" title="Unité frigorifique">
                                                        ❄️ Frigo {vehicle.reefer_temperature_range ? `[${vehicle.reefer_temperature_range.min}°C..${vehicle.reefer_temperature_range.max}°C]` : ''}
                                                    </span>
                                                ) : (
                                                    <span className="text-[10px] font-medium text-slate-400">Sans frigo</span>
                                                )}

                                                {vehicle.is_hazardous_compatible ? (
                                                    <span className="px-2 py-0.5 text-[10px] font-bold rounded-md bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300 flex items-center gap-1" title="Compatible matières dangereuses ADR">
                                                        🔥 ADR
                                                    </span>
                                                ) : null}
                                            </div>

                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    onSelectVehicle(vehicle)
                                                    onClose()
                                                }}
                                                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all flex items-center gap-1 ${isSelected
                                                    ? 'bg-emerald-600 text-white shadow-xs hover:bg-emerald-700'
                                                    : 'bg-orange-700 text-white hover:bg-orange-800 shadow-xs'
                                                    }`}
                                            >
                                                {isSelected ? 'Sélectionné' : 'Appliquer'}
                                            </button>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    ) : (
                        /* ── COMPACT TABLE VIEW ────────────────────────────────────── */
                        <div className="relative overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white shadow-sm dark:bg-slate-900">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-slate-200 bg-slate-50/80 dark:border-slate-700 dark:bg-slate-800/60">
                                        {['Immatriculation', 'Type / Sous-type', 'Dimensions', 'Capacité', 'Équipements', 'Favoris', 'Statut'].map((heading) => (
                                            <th key={heading} className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                                {heading}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredVehicles.map((vehicle) => {
                                        const isSelected =
                                            currentVehicleId === vehicle.id ||
                                            (currentVehicleReg && vehicle.registration_number === currentVehicleReg)
                                        const displayType = getEffectiveVehicleType(vehicle)
                                        const { icon: TypeIcon, color: typeColor } = getVehicleTypeStyle(displayType)

                                        return (
                                            <tr
                                                key={vehicle.id || vehicle.registration_number}
                                                onClick={() => {
                                                    onSelectVehicle(vehicle)
                                                    onClose()
                                                }}
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter' || event.key === ' ') {
                                                        event.preventDefault()
                                                        onSelectVehicle(vehicle)
                                                        onClose()
                                                    }
                                                }}
                                                tabIndex={0}
                                                aria-label={`Sélectionner le véhicule ${getVehicleDisplayName(vehicle)}`}
                                                className={`group cursor-pointer border-b border-slate-100 last:border-0 transition-all duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 dark:border-slate-800 ${isSelected
                                                    ? 'bg-blue-50/80 dark:bg-blue-950/40'
                                                    : 'hover:bg-orange-50/30 dark:hover:bg-slate-800/30'
                                                    }`}
                                            >
                                                <td className="px-4 py-3.5">
                                                    <div className="flex items-center gap-2.5">
                                                        <div
                                                            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border transition-transform group-hover:scale-105"
                                                            style={{ backgroundColor: typeColor + '18', borderColor: typeColor + '40', color: typeColor }}
                                                        >
                                                            <TypeIcon className="h-4 w-4" />
                                                        </div>
                                                        <span className="font-mono text-sm font-bold tracking-wide text-slate-900 dark:text-white">{getVehicleDisplayName(vehicle)}</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3.5">
                                                    <div className="flex flex-col">
                                                        <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                                                            {getVehicleTypeLabel(displayType)}
                                                        </span>
                                                        {vehicle.vehicle_subtype && (
                                                            <span className="mt-0.5 text-[11px] font-medium text-slate-500 dark:text-slate-400">
                                                                {getVehicleSubtypeLabel(vehicle.vehicle_subtype)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3.5">
                                                    {vehicle.length && vehicle.width && vehicle.height ? (
                                                        <span className="whitespace-nowrap rounded-md bg-slate-100 px-2 py-1 font-mono text-xs tabular-nums text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                                                            {formatDim(vehicle.length)} × {formatDim(vehicle.width)} × {formatDim(vehicle.height)}
                                                        </span>
                                                    ) : (
                                                        <span className="text-slate-400">—</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3.5 text-sm text-slate-600 dark:text-slate-400">
                                                    <div className="flex flex-col gap-0.5">
                                                        {vehicle.capacity_weight ? (
                                                            <span className="flex items-center gap-1.5 text-xs">
                                                                <Weight className="h-3.5 w-3.5 text-emerald-500" />
                                                                <span className="font-semibold text-slate-700 dark:text-slate-300">{formatWeightTonnes(vehicle.capacity_weight)}</span>
                                                            </span>
                                                        ) : null}
                                                        {vehicle.capacity_volume ? (
                                                            <span className="flex items-center gap-1.5 text-xs">
                                                                <SquareDimensions className="h-3.5 w-3.5 text-sky-500" />
                                                                <span className="font-semibold text-slate-700 dark:text-slate-300">{formatMeasure(vehicle.capacity_volume, 'm3', 'volume')}</span>
                                                            </span>
                                                        ) : null}
                                                        {!vehicle.capacity_weight && !vehicle.capacity_volume && <span className="text-slate-400">—</span>}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3.5">
                                                    <div className="flex items-center gap-2">
                                                        {vehicle.has_reefer_unit && (
                                                            <div className="flex items-center gap-1.5 rounded-lg border border-sky-200 bg-sky-50 px-2 py-1 dark:border-sky-800 dark:bg-sky-900/20" title="Unité de Réfrigération">
                                                                <Thermometer className="h-4 w-4 text-sky-500 dark:text-sky-400" />
                                                            </div>
                                                        )}
                                                        {vehicle.is_hazardous_compatible && (
                                                            <div className="hazmat-stripe flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1 dark:border-amber-800 dark:bg-amber-900/20" title="Compatible ADR (Marchandises Dangereuses)">
                                                                <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 dark:text-amber-400" />
                                                            </div>
                                                        )}
                                                        {!vehicle.has_reefer_unit && !vehicle.is_hazardous_compatible && (
                                                            <span className="text-slate-400">—</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3.5">
                                                    <div className="flex items-center gap-2">
                                                        <StarRating rating={vehicle.favorite_rating || 0} readonly size="sm" />
                                                        {(vehicle.favorite_rating || 0) > 0 && (
                                                            <span className="rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold text-amber-600 dark:bg-amber-900/20 dark:text-amber-400">
                                                                {vehicle.favorite_rating}/5
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3.5">
                                                    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${getStatusBadgeClasses(vehicle.status)}`}>
                                                        <span className="h-1.5 w-1.5 rounded-full bg-current" />
                                                        {getStatusLabel(vehicle.status)}
                                                    </span>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* ── FOOTER ─────────────────────────────────────────────────────────── */}
                <div className="flex items-center justify-between pt-3 border-t border-slate-200 dark:border-slate-800">
                    <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                        <TruckIcon className="w-4 h-4 text-blue-500" />
                        <span>Sélectionnez un véhicule pour appliquer instantanément ses gabarits et contraintes d'essieux.</span>
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-xs font-bold text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-xl transition-colors"
                    >
                        Fermer
                    </button>
                </div>
            </div>
        </Dialog>
    )
}
